import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookOpen, Upload, Trash2, Search, Loader2, FileText, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { cn } from "@/lib/utils";
import CCBSkillHint from "@/components/CCBSkillHint";

const BASE = process.env.REACT_APP_BACKEND_URL;

type SubTab = "documents" | "ingest" | "search";

interface Props {
  mongoProjectId: string;
  projectName: string;
}

export default function KnowledgeBasePanel({ mongoProjectId, projectName }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
  const [baProjectId, setBaProjectId] = useState<string | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<SubTab>("documents");

  // Documents sub-tab state
  const [documents, setDocuments] = useState<any[]>([]);
  const [docsLoading, setDocsLoading] = useState(true);

  // Ingest sub-tab state
  const [ingestMode, setIngestMode] = useState<"paste" | "file">("paste");
  const [ingestTitle, setIngestTitle] = useState("");
  const [ingestDocType, setIngestDocType] = useState("policy");
  const [ingestSource, setIngestSource] = useState("");
  const [ingestContent, setIngestContent] = useState("");
  const [ingesting, setIngesting] = useState(false);
  const [ingestSuccess, setIngestSuccess] = useState<string | null>(null);
  const [ingestError, setIngestError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Search sub-tab state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchDone, setSearchDone] = useState(false);

  useEffect(() => {
    initProject();
  }, [mongoProjectId]);

  const initProject = async () => {
    try {
      const { data } = await axios.get(
        `${BASE}/api/tools/projects/by-mongo/${mongoProjectId}?name=${encodeURIComponent(projectName)}`,
        { headers: authHeader }
      );
      setBaProjectId(data.id);
      loadDocuments(data.id);
    } catch {
      setDocsLoading(false);
    }
  };

  const loadDocuments = async (pid: string) => {
    setDocsLoading(true);
    try {
      const { data } = await axios.get(
        `${BASE}/api/tools/documents?project_id=${pid}`,
        { headers: authHeader }
      );
      setDocuments(data || []);
    } catch {
      setDocuments([]);
    } finally {
      setDocsLoading(false);
    }
  };

  const handleDelete = async (docId: string) => {
    if (!baProjectId) return;
    try {
      await axios.delete(`${BASE}/api/tools/documents/${docId}`, { headers: authHeader });
      loadDocuments(baProjectId);
    } catch {
      // silently fail — could show toast in future
    }
  };

  const handleIngest = async () => {
    if (!ingestTitle.trim() || !ingestContent.trim() || !baProjectId) return;
    setIngesting(true);
    setIngestSuccess(null);
    setIngestError(null);
    try {
      const { data } = await axios.post(
        `${BASE}/api/tools/documents`,
        {
          project_id: baProjectId,
          title: ingestTitle.trim(),
          doc_type: ingestDocType,
          source: ingestSource.trim() || null,
          content: ingestContent,
        },
        { headers: authHeader }
      );
      setIngestSuccess(`Ingested as ${data.chunk_count} chunk${data.chunk_count !== 1 ? "s" : ""}`);
      setIngestTitle("");
      setIngestDocType("policy");
      setIngestSource("");
      setIngestContent("");
      setActiveSubTab("documents");
      loadDocuments(baProjectId);
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || "Ingestion failed";
      setIngestError(msg);
    } finally {
      setIngesting(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !baProjectId) return;
    setIngesting(true);
    setIngestSuccess(null);
    setIngestError(null);
    setUploadProgress(0);
    try {
      const form = new FormData();
      form.append("file", selectedFile);
      form.append("project_id", baProjectId);
      form.append("title", ingestTitle.trim() || selectedFile.name.replace(/\.[^.]+$/, ""));
      form.append("doc_type", ingestDocType);
      form.append("source", ingestSource.trim() || selectedFile.name);
      const { data } = await axios.post(
        `${BASE}/api/tools/documents/upload`,
        form,
        {
          headers: { ...authHeader, "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
          },
        }
      );
      setIngestSuccess(`Ingested "${data.title}" as ${data.chunk_count} chunk${data.chunk_count !== 1 ? "s" : ""}`);
      setIngestTitle("");
      setIngestSource("");
      setSelectedFile(null);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setActiveSubTab("documents");
      loadDocuments(baProjectId);
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || "Upload failed";
      setIngestError(msg);
    } finally {
      setIngesting(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || !baProjectId) return;
    setSearching(true);
    setSearchDone(false);
    setSearchResults([]);
    try {
      const { data } = await axios.post(
        `${BASE}/api/tools/search`,
        { query: searchQuery.trim(), project_id: baProjectId, limit: 5 },
        { headers: authHeader }
      );
      setSearchResults(data || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
      setSearchDone(true);
    }
  };

  const usingHybrid = searchResults.length > 0 && searchResults.some((r) => r.search_mode === "hybrid");
  const usingFTS = searchResults.length > 0 && !usingHybrid;

  const subTabs: { id: SubTab; label: string }[] = [
    { id: "documents", label: "Documents" },
    { id: "ingest", label: "Ingest" },
    { id: "search", label: "Search" },
  ];

  return (
    <div className="p-4 space-y-4">
    <CCBSkillHint tab="knowledge-base" className="mb-4" />
      {/* Header */}
      <div>
        <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider">Knowledge Base</h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Ingest documents and search with semantic or full-text retrieval
        </p>
      </div>

      {/* Sub-tab switcher */}
      <div className="flex gap-0 border-b border-border">
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={cn(
              "px-4 py-2 text-xs font-body border-b-2 transition-colors",
              activeSubTab === tab.id
                ? "border-accent text-accent font-semibold"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Documents sub-tab */}
      {activeSubTab === "documents" && (
        <div className="space-y-2">
          {docsLoading ? (
            <div className="text-center py-16 text-sm text-muted-foreground">Loading…</div>
          ) : documents.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-sm font-semibold text-foreground mb-1">No documents yet</p>
              <p className="text-xs text-muted-foreground">Use the Ingest tab to add your first document.</p>
            </div>
          ) : (
            documents.map((doc: any) => (
              <div key={doc.id} className="border border-border rounded-md p-4 bg-card flex items-start justify-between gap-3">
                <div className="space-y-1.5 min-w-0">
                  <p className="text-sm font-semibold text-card-foreground truncate">{doc.title}</p>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {doc.doc_type && (
                      <Badge variant="outline" className="text-[10px] px-1.5">
                        {doc.doc_type}
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-[10px] px-1.5 bg-muted/50">
                      {doc.chunk_count ?? 0} chunk{doc.chunk_count !== 1 ? "s" : ""}
                    </Badge>
                    {doc.source && (
                      <span className="text-[11px] text-muted-foreground font-mono truncate max-w-[200px]">
                        {doc.source}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => handleDelete(doc.id)}
                  title="Delete document"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))
          )}
        </div>
      )}

      {/* Ingest sub-tab */}
      {activeSubTab === "ingest" && (
        <div className="space-y-3">
          {/* Mode toggle */}
          <div className="flex items-center gap-0 border border-border rounded-md overflow-hidden w-fit">
            {(["paste", "file"] as const).map((m) => (
              <button key={m} onClick={() => { setIngestMode(m); setIngestError(null); setIngestSuccess(null); }}
                className={cn("px-3 py-1.5 text-xs font-body flex items-center gap-1.5 transition-colors",
                  ingestMode === m ? "bg-accent text-accent-foreground font-semibold" : "text-muted-foreground hover:bg-secondary"
                )}>
                {m === "paste" ? <FileText className="h-3 w-3" /> : <Upload className="h-3 w-3" />}
                {m === "paste" ? "Paste Text" : "Upload File"}
              </button>
            ))}
          </div>

          {/* Shared metadata fields */}
          <Input placeholder={ingestMode === "file" ? "Document title (optional — defaults to filename)" : "Document title *"}
            value={ingestTitle} onChange={(e) => setIngestTitle(e.target.value)} />
          <Select value={ingestDocType} onValueChange={setIngestDocType}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="policy">Policy</SelectItem>
              <SelectItem value="sop">SOP</SelectItem>
              <SelectItem value="guideline">Guideline</SelectItem>
              <SelectItem value="reference">Reference</SelectItem>
              <SelectItem value="notes">Notes</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="URL or reference (optional)"
            value={ingestSource} onChange={(e) => setIngestSource(e.target.value)} />

          {/* Paste mode */}
          {ingestMode === "paste" && (
            <div className="space-y-1">
              <Textarea rows={8}
                placeholder="Paste document text, policy excerpt, SOP, or any reference material..."
                value={ingestContent} onChange={(e) => setIngestContent(e.target.value)} />
              <p className="text-xs text-muted-foreground">{ingestContent.length.toLocaleString()} characters</p>
            </div>
          )}

          {/* File upload mode */}
          {ingestMode === "file" && (
            <div className="space-y-2">
              <div
                className={cn("border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
                  selectedFile ? "border-accent/50 bg-accent/5" : "border-border hover:border-accent/30 hover:bg-secondary/30"
                )}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const f = e.dataTransfer.files[0];
                  if (f) setSelectedFile(f);
                }}
              >
                <input ref={fileInputRef} type="file"
                  accept=".pdf,.txt,.md,.docx,.csv"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) setSelectedFile(f); }} />
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="h-5 w-5 text-accent" />
                    <div className="text-left">
                      <p className="text-sm font-semibold text-foreground">{selectedFile.name}</p>
                      <p className="text-xs text-muted-foreground">{(selectedFile.size / 1024).toFixed(0)} KB</p>
                    </div>
                    <button className="ml-2 text-muted-foreground hover:text-destructive"
                      onClick={(e) => { e.stopPropagation(); setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}>
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
                    <p className="text-sm text-muted-foreground">Drop a file here or click to browse</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">.pdf · .txt · .md · .docx · .csv</p>
                  </>
                )}
              </div>

              {/* Upload progress */}
              {ingesting && uploadProgress > 0 && uploadProgress < 100 && (
                <div className="space-y-1">
                  <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full bg-accent transition-all duration-200 rounded-full"
                      style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <p className="text-xs text-muted-foreground text-center">{uploadProgress}% uploaded</p>
                </div>
              )}
            </div>
          )}

          <Button
            onClick={ingestMode === "paste" ? handleIngest : handleUpload}
            disabled={ingesting || (ingestMode === "paste" ? (!ingestTitle.trim() || !ingestContent.trim()) : !selectedFile)}
            className="w-full gap-2"
          >
            {ingesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {ingesting ? (ingestMode === "file" && uploadProgress < 100 ? `Uploading ${uploadProgress}%…` : "Processing…") : "Ingest & Embed"}
          </Button>

          {ingesting && (
            <p className="text-xs text-muted-foreground text-center">
              {ingestMode === "file" ? "Extracting text and embedding… large PDFs may take 30–60s." : "Processing… first run may take 15–30s while embedding model initializes."}
            </p>
          )}

          {ingestSuccess && (
            <div className="rounded-md border border-green-500/30 bg-green-500/10 p-3 text-xs text-green-700 dark:text-green-400">
              ✓ {ingestSuccess}
            </div>
          )}
          {ingestError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
              {ingestError}
            </div>
          )}
        </div>
      )}

      {/* Search sub-tab */}
      {activeSubTab === "search" && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder="Search documents…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="flex-1"
            />
            <Button
              size="sm"
              onClick={handleSearch}
              disabled={searching || !searchQuery.trim()}
              className="gap-1.5 shrink-0"
            >
              {searching ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Search className="h-3.5 w-3.5" />
              )}
              Search
            </Button>
          </div>

          {searchResults.length > 0 && usingHybrid && (
            <div className="rounded-md border border-green-500/30 bg-green-500/10 p-2 text-xs text-green-700 dark:text-green-400">
              Vector + full-text hybrid search
            </div>
          )}

          {searchResults.length > 0 && usingFTS && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-400">
              Full-text search only (embed documents to enable vector search)
            </div>
          )}

          {searchDone && searchResults.length === 0 && (
            <div className="text-center py-10 text-sm text-muted-foreground">
              No results found for "{searchQuery}"
            </div>
          )}

          {searchResults.length > 0 && (
            <div className="space-y-2">
              {searchResults.map((r: any, i: number) => {
                const isHybrid = r.search_mode === "hybrid";
                const scoreDisplay = isHybrid
                  ? `Score: ${(r.rrf_score * 1000).toFixed(1)}`
                  : `${Math.max(0, Math.round(r.rrf_score * 100))}%`;
                const visibleTags: string[] = Array.isArray(r.tags) ? r.tags.slice(0, 4) : [];
                return (
                  <div key={r.id ?? i} className="border border-border rounded-md p-3 bg-card space-y-1.5">
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-sm font-semibold text-card-foreground">
                        {r.doc_title || r.title}
                      </span>
                      <div className="flex items-center gap-1 shrink-0">
                        {isHybrid ? (
                          <Badge className="text-[10px] px-1.5 bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30 hover:bg-green-500/15">
                            Hybrid
                          </Badge>
                        ) : (
                          <Badge className="text-[10px] px-1.5 bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30 hover:bg-amber-500/15">
                            Full-text
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-[10px] px-1.5">
                          {scoreDisplay}
                        </Badge>
                      </div>
                    </div>
                    {r.section && (
                      <p className="text-[11px] text-muted-foreground font-mono">{r.section}</p>
                    )}
                    <p className="text-xs text-foreground/80 leading-relaxed">
                      {r.text?.length > 200 ? r.text.slice(0, 200) + "…" : r.text}
                    </p>
                    {visibleTags.length > 0 && (
                      <div className="flex flex-wrap gap-1 pt-0.5">
                        {visibleTags.map((tag) => (
                          <Badge
                            key={tag}
                            variant="outline"
                            className="text-[9px] px-1 py-0 font-mono text-muted-foreground border-muted-foreground/30"
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
